import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as lodash from 'lodash';
import * as Yup from 'yup';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import getThemeLookup from '../selectors/getThemeLookup';
import StandardButton from '../components/StandardButton';
import getProfileInitialValues from '../selectors/getProfileInitialValues';
import getMemberFields from '../selectors/getMemberFields';
import getMemberSchema from '../selectors/getMemberSchema';
import getProfile from '../selectors/getProfile';
import { RedcatApiHandler } from 'polygon-utils';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';
import syncMember from '../actions/syncMember';
import ProfileFields from './ProfileFields';
import { Formik, Form, FormikHelpers } from 'formik';
import Text from '../components/Text';
import SendVerificationButton from '../components/SendVerificationButton';
import RegistrationVerify from './RegistrationVerify';
import FormTextField from '../components/FormTextField';
import TouchableOpacity from '../components/TouchableOpacity';

export const PROFILE_ROUTE = '/profile';

const ProfileScreen = () => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const dispatch = useAppDispatch();
  const initialVal = useAppSelector(getProfileInitialValues);
  const memberFields = useAppSelector(getMemberFields);
  const memberSchema = useAppSelector(getMemberSchema);
  const profile = useAppSelector(getProfile);
  const [mfaToken, setMfaToken] = useState(null);
  const submit = (values: Partial<RegisterFields>, formikHelpers: FormikHelpers<FormikFields>) => {
    const { setSubmitting, resetForm } = formikHelpers;
    const path = '/api/v1/profile';
    RedcatApiHandler.authorisedFetch({
      path,
      method: 'PUT',
      body: mfaToken
        ? //@ts-ignore
          { ...lodash.omit(values, 'mfaCode'), tfa_pin: values.mfaCode, tfa_token: mfaToken }
        : lodash.omit(values, 'mfaCode'),
    })
      .then(response => {
        setMfaToken(null);
        //@ts-ignore
        if (response.token) {
          dispatch(syncMember());
        }
        if (response.success) {
          enqueueSuccessSnackbar(t('updateSuccess'));
          setSubmitting(false);
          resetForm({ values });
        }
      })
      .catch(err => {
        setSubmitting(false);
        const data = lodash.get(err, 'details.json', {});
        if (data.error_code === 3) {
          setMfaToken(data.additional_info);
          enqueueSuccessSnackbar('Verification PIN required');
        } else {
          enqueueErrorSnackbar(err);
        }
      });
  };

  return !profile?.verified ? (
    <RegistrationVerify showContinueButton={false} internalRedirect />
  ) : (
    <div style={{ position: 'relative' }}>
      {mfaToken && (
        <TouchableOpacity
          onClick={() => setMfaToken(null)}
          style={{ position: 'absolute', top: -30, left: -5 }}
        >
          <IoIosArrowRoundBack
            style={{
              fontSize: 28,
            }}
          />
        </TouchableOpacity>
      )}
      {!profile?.verified ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text value={t('profileVerificationText')} style={{ fontSize: 16 }} />
          <SendVerificationButton containerStyle={{ paddingTop: 20 }} />
        </div>
      ) : null}
      {!mfaToken && (
        <div style={{ padding: '20px 0' }}>
          <Text value={t('profileDetails')} style={{ fontSize: 16 }} />
        </div>
      )}
      {mfaToken && (
        <div style={{ padding: '30px 0 0' }}>
          <Text value={t('profileEmailVerification')} style={{ fontSize: 16 }} />
        </div>
      )}
      <Formik
        validationSchema={
          mfaToken
            ? memberSchema.visible?.shape({
                mfaCode: Yup.string().required('Required').min(4, 'Too Short'),
              })
            : memberSchema.visible
        }
        initialValues={{ ...initialVal.visible, mfaCode: '' }}
        //@ts-ignore
        onSubmit={submit}
        enableReinitialize={true}
      >
        {({ handleSubmit, handleChange, isSubmitting, isValid, dirty }) => {
          return (
            <Form onSubmit={handleSubmit}>
              {(mfaToken
                ? memberFields?.visible.map((field: FormikFields) => ({
                    ...field,
                    editMode: 'hidden',
                  }))
                : memberFields?.visible
              ).map((field: FormikFields) => (
                <ProfileFields field={field} key={field.id} />
              ))}
              {Boolean(mfaToken) && (
                <FormTextField
                  id="mfaCode"
                  name="mfaCode"
                  type="mfaCode"
                  label={t('field.signIn.mfaCode')}
                  required
                  onChange={handleChange}
                />
              )}
              <div className="position-relative" style={{ marginTop: 20 }}>
                <StandardButton
                  containerStyle={styles.button}
                  themeKey="loyaltyButton"
                  label={t(mfaToken ? 'verify' : 'button.updateProfile')}
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isValid || !dirty}
                  showSpinner={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const styles: Styles = {
  button: {
    borderRadius: 5,
    height: 40,
  },
};

export default ProfileScreen;
